import { doc, getDocFromServer } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import IdeasPage from "./IdeasPage";
import type { PendingTopicData } from "../components/autopilot/TopicCard";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { withRequestDeadline } from "../lib/requestDeadline";
import { db } from "../services/Firebase";

const unavailable =
  "This idea is unavailable, or your signed-in account no longer has access. Try the account that owns the idea or belongs to its workspace.";

/** Resolve access and workspace before mounting the idea's existing detail view. */
export default function PrivateIdeaPage({ ideaId }: { ideaId: string }) {
  const {
    currentUser,
    loading,
    activeWorkspaceId,
    workspaceSwitching,
    switchWorkspace,
    logout,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    key: string;
    idea?: PendingTopicData;
    error?: string;
  } | null>(null);
  const key = JSON.stringify([
    currentUser?.uid,
    ideaId,
    activeWorkspaceId,
    attempt,
  ]);

  useEffect(() => {
    if (loading || !currentUser || !activeWorkspaceId) return;
    let cancelled = false;
    const controller = new AbortController();
    const load = async () => {
      try {
        // A server read rechecks membership instead of trusting an offline copy.
        const snapshot = await withRequestDeadline(
          () => getDocFromServer(doc(db, "pending_topics", ideaId)),
          20000,
          "Opening this idea took too long. Please try again.",
          controller.signal,
        );
        if (cancelled) return;
        if (!snapshot.exists()) throw new Error(unavailable);
        const data = snapshot.data();
        const idea = { ...data, id: snapshot.id } as PendingTopicData;
        const workspaceId = data.workspaceId || data.userId;
        if (!workspaceId) throw new Error(unavailable);
        // The document read and activation endpoint both enforce access. The
        // destination comes from the authorized document, never a URL hint.
        if (workspaceId !== activeWorkspaceId) {
          await switchWorkspace(workspaceId);
          // The new workspace reruns this effect before mounting viewer actions.
          return;
        }
        if (!cancelled) setResult({ key, idea: idea as PendingTopicData });
      } catch (error) {
        if (cancelled) return;
        const code = (error as { code?: string }).code;
        setResult({
          key,
          error:
            code === "permission-denied" || code === "not-found"
              ? unavailable
              : error instanceof Error
                ? error.message
                : "Couldn’t open this idea. Please try again.",
        });
      }
    };
    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    currentUser,
    loading,
    activeWorkspaceId,
    ideaId,
    switchWorkspace,
    attempt,
    key,
  ]);

  const current = result?.key === key ? result : null;
  const close = () => {
    const params = new URLSearchParams(location.search);
    params.delete("viewIdea");
    navigate(
      { pathname: "/ideas", search: params.toString() ? `?${params}` : "" },
      { replace: true },
    );
  };
  const changeAccount = async () => {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    try {
      await logout();
      navigate(`/login?${new URLSearchParams({ returnTo })}`, {
        replace: true,
      });
    } catch {
      setResult({ key, error: "Couldn’t sign out. Please try again." });
    }
  };

  return (
    <DashboardLayout>
      {current?.error ? (
        <section role="alert" className="mx-auto max-w-lg p-8">
          <h1 className="text-xl font-semibold">Unable to open this idea</h1>
          <p className="my-4 text-sm text-muted-foreground">{current.error}</p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setAttempt((value) => value + 1)}>
              Try again
            </Button>
            <Button variant="outline" onClick={changeAccount}>
              Use another account
            </Button>
            <Button variant="ghost" onClick={close}>
              Back to Ideas
            </Button>
          </div>
        </section>
      ) : current?.idea && !loading && !workspaceSwitching ? (
        <IdeasPage
          key={key}
          linkedIdea={current.idea}
          onCloseLinkedIdea={close}
        />
      ) : (
        <p role="status" className="p-8 text-sm text-muted-foreground">
          Opening your idea…
        </p>
      )}
    </DashboardLayout>
  );
}

export function IdeasRoute() {
  const location = useLocation();
  const ideaId = new URLSearchParams(location.search).get("viewIdea");
  return ideaId ? (
    <PrivateIdeaPage ideaId={ideaId} />
  ) : (
    <DashboardLayout>
      <IdeasPage />
    </DashboardLayout>
  );
}
